import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore, getAndClearReturnUrl } from "@/lib/stores/auth";
import { useBooks, useStartBookProgress } from "@/lib/api/books";
import { usePracticeLabs } from "@/lib/api/practice-labs";
import { Card, CardContent } from "@/components/ui/card";
import { BookCard } from "@/components/book/book-card";
import { PracticeLabCard } from "@/components/dashboard/practice-lab-card";
import { DiscordJoinBanner } from "@/components/dashboard/discord-join-banner";
import { isFreeUser } from "@/lib/user-type";
import { Loader2Icon, BookOpenIcon } from "lucide-react";
import { t } from "@/lib/typography";

export function Dashboard() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading } = useAuthStore();
  const [checkedReturnUrl, setCheckedReturnUrl] = useState(false);

  const { data: books, isLoading: booksLoading } = useBooks({
    enabled: isAuthenticated,
  });

  // Independent (non-chapter) practice labs — an optional section, shown only
  // when the member has access to at least one.
  const { data: practiceLabs } = usePracticeLabs({
    standaloneOnly: true,
    enabled: isAuthenticated,
  });

  const startProgress = useStartBookProgress();

  useEffect(() => {
    if (!isLoading && isAuthenticated && !checkedReturnUrl) {
      setCheckedReturnUrl(true);
      const returnUrl = getAndClearReturnUrl();
      if (returnUrl) {
        navigate(returnUrl);
        return;
      }
    }

    if (!isLoading && !isAuthenticated) {
      navigate("/login");
    }
  }, [isLoading, isAuthenticated, navigate, checkedReturnUrl]);

  const handleStartBook = async (bookId: number) => {
    try {
      await startProgress.mutateAsync(bookId);
      navigate(`/books/${bookId}`);
    } catch {
      navigate(`/books/${bookId}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center">
        <Loader2Icon className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Discord banner: shown to paid users (whop) who haven't linked Discord
          yet. Hidden for free users — they don't have Lab access, so there's
          no Discord server to join. */}
      {!user.discord_id && !isFreeUser(user) && <DiscordJoinBanner />}

      <h2 className={t.sectionTitle}>Your Books</h2>

      {booksLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2Icon className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : !books || books.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <BookOpenIcon className="mx-auto mb-4 size-12 text-muted-foreground/50" />
            <p className="text-muted-foreground">No books available yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {books.map((book) => (
            <BookCard
              key={book.id}
              book={book}
              onStart={() => handleStartBook(book.id)}
              isStarting={startProgress.isPending}
            />
          ))}
        </div>
      )}

      {/* Independent practice labs — only rendered when the member has any. */}
      {practiceLabs && practiceLabs.length > 0 && (
        <div className="space-y-4">
          <h2 className={t.sectionTitle}>Practice Labs</h2>
          {practiceLabs.map((lab) => (
            <PracticeLabCard key={lab.id} lab={lab} />
          ))}
        </div>
      )}
    </div>
  );
}
